<script lang="ts">
  /**
   * Superadmin: who has run the public keyword audit.
   *
   * The audit is anonymous — no sign-in, no email unless they ask to be contacted — so
   * this is the trail it leaves. Grouped by community rather than listed per run,
   * because the question it answers is who keeps coming back: a per-run log makes one
   * returning owner look like several strangers, and a second run is the whole signal.
   *
   * Each row can hand out a signup link bound to that community — the same registration
   * invite `/admin/invites` mints (`/register?invite=`). The owner types their own email
   * and password when they open it, so no email is needed up front. An open invite for
   * the community is reused rather than minting another.
   */
  import { onMount } from 'svelte';
  import { navigate } from '../lib/router.svelte';
  import {
    createInvite,
    fetchAllInvites,
    fetchAuditLeadMembership,
    fetchAuditLeads,
    fetchOutreachQuota,
    sendLeadDm,
    type OutreachQuota,
    type AuditLead,
    type Invite,
  } from '@api/v1/admin';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { redirectIfAwaitingOnboarding, type AdminUser } from '../lib/admin';
  import AdminShell from './AdminShell.svelte';

  let me = $state<AdminUser | null>(null);
  let rows = $state<AuditLead[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let q = $state('');
  let repeatOnly = $state(false);

  // Newest invite per community slug. The list comes back newest-first, so the first
  // one seen for a slug wins.
  let invites = $state<Record<string, Invite>>({});
  let inviting = $state<string | null>(null); // slug whose link is being minted
  let copied = $state<string | null>(null); // slug whose link was just copied
  let inviteError = $state<string | null>(null);
  let copyTimer: ReturnType<typeof setTimeout> | undefined;

  // "DM owner": one DM per lead to its community owner, at most `quota.limit` a day.
  // The message itself is the "Audit lead outreach" sequence on our own community.
  let quota = $state<OutreachQuota | null>(null);
  let dming = $state<string | null>(null);
  let dmError = $state<string | null>(null);

  function isJoined(r: AuditLead): boolean {
    const role = roles[r.slug];
    return r.membership === 'joined' || role === 'member' || (!!role && STAFF.has(role));
  }

  async function dmOwner(r: AuditLead): Promise<void> {
    if (!quota || quota.used >= quota.limit) return;
    const ok = confirm(
      `DM the owner of ${r.display_name} the outreach message?\n\n` +
        `${quota.used}/${quota.limit} sent in the last 24 hours.`,
    );
    if (!ok) return;
    dming = r.slug;
    dmError = null;
    try {
      const res = await sendLeadDm(r.slug);
      quota = { used: res.used, limit: res.limit };
      if (res.status === 'sent') {
        rows = rows.map((x) => (x.slug === r.slug ? { ...x, dm_sent_at: res.sent_at } : x));
      } else {
        dmError = `Skool did not accept the DM to ${r.display_name}: ${res.error ?? 'unknown error'}`;
      }
    } catch (err: unknown) {
      dmError = err instanceof Error ? err.message : 'Could not send the DM.';
    } finally {
      dming = null;
    }
  }

  // Live role per slug for rows our memberships list names: undefined = not asked yet,
  // 'checking' while in flight, then the role (or null when the read failed).
  let roles = $state<Record<string, string | null>>({});
  const STAFF = new Set(['owner', 'admin', 'moderator', 'group-admin', 'group-moderator']);

  /** One at a time: each read is a page fetch through the one shared scraper Chrome. */
  async function checkMemberships(leads: AuditLead[]): Promise<void> {
    for (const r of leads.filter((l) => l.membership === 'listed')) {
      roles = { ...roles, [r.slug]: 'checking' };
      try {
        roles = { ...roles, [r.slug]: (await fetchAuditLeadMembership(r.slug)).role };
      } catch {
        roles = { ...roles, [r.slug]: null };
      }
    }
  }

  function membershipLabel(r: AuditLead): { text: string; cls: string } {
    if (r.membership == null) return { text: '—', cls: 'text-base-content/30' };
    if (r.membership === 'none') return { text: 'Not joined', cls: 'text-base-content/50' };
    if (r.membership === 'joined') return { text: 'Joined', cls: 'badge badge-success badge-sm' };
    const role = roles[r.slug];
    if (role === undefined || role === 'checking') return { text: 'Checking…', cls: 'text-base-content/50' };
    if (role === 'pending') return { text: 'Pending', cls: 'badge badge-warning badge-sm' };
    if (role === 'invited') return { text: 'Invited', cls: 'badge badge-info badge-sm' };
    if (role === 'member' || (role && STAFF.has(role)))
      return { text: 'Joined', cls: 'badge badge-success badge-sm' };
    return { text: 'Joined or pending', cls: 'text-base-content/60' }; // read failed
  }

  const linkFor = (token: string) => `${location.origin}/register?invite=${token}`;
  const slugOf = (url: string) => url.replace(/\/+$/, '').split('/').pop() ?? '';

  function indexInvites(items: Invite[]): Record<string, Invite> {
    const bySlug: Record<string, Invite> = {};
    for (const inv of items) {
      const slug = slugOf(inv.skool_url);
      if (slug && !(slug in bySlug)) bySlug[slug] = inv;
    }
    return bySlug;
  }

  async function copy(slug: string, token: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(linkFor(token));
    } catch {
      return;
    }
    copied = slug;
    clearTimeout(copyTimer);
    copyTimer = setTimeout(() => (copied = null), 1600);
  }

  async function inviteLink(r: AuditLead): Promise<void> {
    const open = invites[r.slug];
    if (open?.status === 'pending') {
      await copy(r.slug, open.token);
      return;
    }
    inviting = r.slug;
    inviteError = null;
    try {
      const row = await createInvite({ skoolUrl: r.url, note: r.email ?? r.display_name });
      invites = { ...invites, [r.slug]: row };
      await copy(r.slug, row.token);
    } catch (err: unknown) {
      inviteError = err instanceof Error ? err.message : 'Could not create the invite.';
    } finally {
      inviting = null;
    }
  }

  const shown = $derived(
    rows
      .filter((r) => (repeatOnly ? r.runs > 1 : true))
      .filter((r) => {
        const needle = q.trim().toLowerCase();
        return !needle || r.display_name.toLowerCase().includes(needle) || r.slug.includes(needle);
      }),
  );
  const repeats = $derived(rows.filter((r) => r.runs > 1).length);
  const withEmail = $derived(rows.filter((r) => r.email).length);

  onMount(async () => {
    try {
      // Same gate its siblings use: the route is superadmin-only server-side, and this
      // keeps a creator who guesses the URL from seeing a shell before the 401 lands.
      me = asAdminUser(await ensureBootstrap());
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      if (me.role !== 'superadmin') {
        navigate('/admin');
        return;
      }
      // Invites are a convenience here: if they fail to load, the leads still show and
      // a click simply mints a fresh link.
      const [leads, inv, q] = await Promise.all([
        fetchAuditLeads(),
        fetchAllInvites().catch(() => null),
        fetchOutreachQuota().catch(() => null),
      ]);
      rows = leads.items;
      quota = q;
      if (inv) invites = indexInvites(inv.items);
      void checkMemberships(rows); // fills the status column in after the table shows
    } catch {
      error = 'Could not load the audit log.';
    } finally {
      loading = false;
    }
  });

  function when(iso: string): string {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function price(r: AuditLead): string {
    if (!r.price_amount) return 'Free';
    const major = r.price_amount / 100;
    const period = r.price_interval === 'month' ? '/mo' : r.price_interval === 'year' ? '/yr' : '';
    return `$${Number.isInteger(major) ? major : major.toFixed(2)}${period}`;
  }
</script>

<AdminShell {me}>
  <div class="p-4 sm:p-6">
    <header class="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 class="font-display text-2xl font-bold">Audit leads</h1>
        <p class="mt-1 text-sm text-base-content/60">
          {rows.length} communities · {repeats} came back · {withEmail} left an email
        </p>
        {#if quota}
          <p class="mt-1 text-sm">
            <span class={quota.used >= quota.limit ? 'font-semibold text-error' : 'font-semibold'}>
              DMs in the last 24h: {quota.used}/{quota.limit}
            </span>
            <span class="text-base-content/50">
              · message: Automations → Skool CRM for Growth → “Audit lead outreach”
            </span>
          </p>
        {/if}
      </div>
      <div class="flex flex-wrap items-center gap-3">
        <label class="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" class="checkbox checkbox-sm" bind:checked={repeatOnly} />
          Repeat visitors only
        </label>
        <input
          class="input input-bordered input-sm w-52"
          placeholder="Search community"
          bind:value={q}
        />
      </div>
    </header>

    {#if inviteError}
      <div class="alert alert-error mb-3"><span>{inviteError}</span></div>
    {/if}
    {#if dmError}
      <div class="alert alert-error mb-3"><span>{dmError}</span></div>
    {/if}

    {#if loading}
      <div class="flex items-center gap-3 text-base-content/60">
        <span class="loading loading-spinner loading-sm"></span> Loading…
      </div>
    {:else if error}
      <div class="alert alert-error"><span>{error}</span></div>
    {:else if shown.length === 0}
      <p class="rounded-box border border-base-300 p-6 text-sm text-base-content/50">
        Nothing yet.
      </p>
    {:else}
      <div class="overflow-x-auto rounded-box border border-base-300">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>Community</th>
              <th class="text-right">Runs</th>
              <th class="text-right">Members</th>
              <th class="text-right">Price</th>
              <th>Last seen</th>
              <th>First seen</th>
              <th>Status</th>
              <th>DM</th>
              <th>Email</th>
              <th>Invite</th>
            </tr>
          </thead>
          <tbody>
            {#each shown as r (r.slug)}
              <tr>
                <td>
                  <!-- privacy-ok: a public Skool community, not a member of one -->
                  <a
                    class="link link-hover font-medium"
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer">{r.display_name}</a
                  >
                  <span class="ml-2 font-mono text-xs text-base-content/40">{r.slug}</span>
                </td>
                <td class="text-right tabular-nums">
                  {#if r.runs > 1}
                    <span class="badge badge-primary badge-sm">{r.runs}</span>
                  {:else}
                    <span class="text-base-content/50">{r.runs}</span>
                  {/if}
                </td>
                <td class="text-right tabular-nums">{r.total_members.toLocaleString()}</td>
                <!-- privacy-ok: a public community's listed price -->
                <td class="text-right tabular-nums">{price(r)}</td>
                <td class="whitespace-nowrap text-sm">{when(r.last_seen)}</td>
                <td class="whitespace-nowrap text-sm text-base-content/60">{when(r.first_seen)}</td>
                <td class="whitespace-nowrap text-sm">
                  {#if roles[r.slug] === 'checking'}
                    <span class="loading loading-spinner loading-xs"></span>
                  {:else}
                    {@const m = membershipLabel(r)}
                    <span class={m.cls}>{m.text}</span>
                  {/if}
                </td>
                <td class="whitespace-nowrap text-sm">
                  {#if r.dm_sent_at}
                    <span class="text-success">Sent {when(r.dm_sent_at)}</span>
                  {:else if isJoined(r)}
                    <button
                      class="btn btn-primary btn-xs"
                      disabled={dming !== null || !quota || quota.used >= quota.limit}
                      onclick={() => dmOwner(r)}
                      title={quota && quota.used >= quota.limit
                        ? 'Daily limit reached'
                        : 'DM this community’s owner the outreach message'}
                    >
                      {#if dming === r.slug}<span class="loading loading-spinner loading-xs"></span>{/if}
                      DM owner
                    </button>
                  {:else}
                    <span class="text-base-content/30">—</span>
                  {/if}
                </td>
                <td class="text-sm">
                  {#if r.email}
                    <!-- privacy-ok: a lead who asked to be contacted; this is the console
                         that exists to contact them -->
                    <a class="link link-hover" href={`mailto:${r.email}`}>{r.email}</a>
                  {:else}
                    <span class="text-base-content/30">—</span>
                  {/if}
                </td>
                <td class="whitespace-nowrap">
                  {#if invites[r.slug]?.status === 'used'}
                    <span class="badge badge-success badge-sm">Signed up</span>
                  {:else}
                    <button
                      class="btn btn-ghost btn-xs"
                      disabled={inviting === r.slug}
                      onclick={() => inviteLink(r)}
                      title="Copy a signup link bound to this community"
                    >
                      {#if inviting === r.slug}
                        <span class="loading loading-spinner loading-xs"></span>
                      {:else if copied === r.slug}
                        Copied!
                      {:else if invites[r.slug]?.status === 'pending'}
                        Copy link
                      {:else}
                        Create link
                      {/if}
                    </button>
                  {/if}
                </td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
</AdminShell>
